import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageSquare, User, LogOut, Menu, X } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { AuthModal } from './auth/AuthModal';
import { signOut } from '@/lib/db/user';

export function Header() {
  const location = useLocation();
  const { user, profile } = useAuthContext();
  console.log('user', { ...user });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleAuthClick = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setIsMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm z-50 border-b border-gray-100">
      <div className="flex justify-between items-center p-4 max-w-7xl mx-auto">
        <Link to="/" className="font-bold text-xl tracking-tight">
          CONVERTOLOGY
        </Link>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <NavigationLinks
            location={location}
            user={user}
            profile={profile}
            handleAuthClick={handleAuthClick}
            handleSignOut={handleSignOut}
          />
        </nav>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <nav className="flex flex-col p-4 space-y-4">
            <NavigationLinks
              location={location}
              user={user}
              profile={profile}
              handleAuthClick={handleAuthClick}
              handleSignOut={handleSignOut}
              isMobile
            />
          </nav>
        </div>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
      />
    </header>
  );
}

interface NavigationLinksProps {
  location: ReturnType<typeof useLocation>;
  user: ReturnType<typeof useAuthContext>['user'];
  profile: ReturnType<typeof useAuthContext>['profile'];
  handleAuthClick: (mode: 'login' | 'signup') => void;
  handleSignOut: () => void;
  isMobile?: boolean;
}

function NavigationLinks({
  location,
  user,
  profile,
  handleAuthClick,
  handleSignOut,
  isMobile,
}: NavigationLinksProps) {
  const linkClass = isMobile
    ? 'block w-full text-left py-2'
    : 'hover:text-gray-600 transition-colors text-sm font-medium';

  const chatButtonClass = isMobile
    ? 'flex items-center gap-2 w-full border-2 border-gray-200 px-4 py-2 rounded-full'
    : 'inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all duration-200 text-sm font-medium';

  return (
    <>
      <Link
        to="/pricing"
        className={`${linkClass} ${
          location.pathname === '/pricing' ? 'text-green-500' : ''
        }`}
      >
        Pricing
      </Link>

      <Link
        to="/chat"
        className={`${chatButtonClass} ${
          location.pathname === '/chat'
            ? 'border-green-500 text-green-500 bg-green-50'
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}
      >
        <MessageSquare className="w-4 h-4" />
        CRO Assistant Chat
      </Link>

      <Link
        to="/about"
        className={`${linkClass} ${
          location.pathname === '/about' ? 'text-green-500' : ''
        }`}
      >
        About
      </Link>

      <Link
        to="/faq"
        className={`${linkClass} ${
          location.pathname === '/faq' ? 'text-green-500' : ''
        }`}
      >
        FAQ
      </Link>

      {user && !user.isAnonymous ? (
        <div className={isMobile ? 'space-y-4' : 'flex items-center gap-4'}>
          <Link
            to="/profile"
            className="flex items-center gap-2 text-sm hover:text-gray-600"
          >
            <User className="w-4 h-4" />
            <span>{profile?.displayName || user.email}</span>
          </Link>
          <button
            onClick={handleSignOut}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      ) : (
        <div className={isMobile ? 'space-y-4' : 'flex items-center gap-4'}>
          <button
            onClick={() => handleAuthClick('login')}
            className="text-sm font-medium hover:text-gray-600"
          >
            Sign In
          </button>
          <button
            onClick={() => handleAuthClick('signup')}
            className="bg-black text-white px-5 py-2 rounded-full hover:bg-gray-800 transition-colors text-sm font-medium w-full md:w-auto"
          >
            Sign Up
          </button>
        </div>
      )}
    </>
  );
}
